package link.erpango.quip.features.maintenances

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import link.erpango.quip.ui.components.SyncStatus
import kotlinx.coroutines.Dispatchers

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import link.erpango.quip.data.db.entities.toDMaintenance
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.domain.catalog.DefaultSparePartCatalog
import link.erpango.quip.domain.entities.DMaintenance
import link.erpango.quip.utils.FileUtils
import link.erpango.quip.utils.AppDispatchers
import link.erpango.quip.domain.entities.DSparePart
import link.erpango.quip.domain.entities.DSparePartCatalog
import link.erpango.quip.domain.entities.DSparePartType
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.domain.services.SignatureService
import link.erpango.quip.domain.services.signNewMaintenance
import link.erpango.quip.domain.entities.DSparePartDocument
import link.erpango.quip.utils.FileData
import link.erpango.quip.domain.usecases.IGarageUseCase

object Counter {
    var count = 0
}

data class DocumentUiModel(
    val document: DSparePartDocument,
    val status: SyncStatus
)

class MaintenancesViewModel(
    private val vehicle: DVehicle,
    private val mainInteractor: IGarageUseCase
): ViewModel() {
    private val _maintenances = MutableStateFlow<List<DMaintenance>>(emptyList())
    val maintenances: StateFlow<List<DMaintenance>> = _maintenances.asStateFlow()

    // Estado para rastrear el resultado de la última operación de agregar mantenimiento
    private val _lastAddResult = MutableStateFlow<Boolean?>(null)
    val lastAddResult: StateFlow<Boolean?> = _lastAddResult

    // Estado para controlar la carga de datos
    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading

    // Estado para los repuestos temporales al crear un mantenimiento
    private val _temporarySpareParts = MutableStateFlow<List<DSparePart>>(emptyList())
    val temporarySpareParts: StateFlow<List<DSparePart>> = _temporarySpareParts.asStateFlow()

    // Catálogo de repuestos (categorías y tipos)
    private val _sparePartCatalog = MutableStateFlow<DSparePartCatalog?>(null)
    val sparePartCatalog: StateFlow<DSparePartCatalog?> = _sparePartCatalog.asStateFlow()

    // Autocomplete data for forms
    private val _distinctMechanics = MutableStateFlow<List<String>>(emptyList())
    val distinctMechanics: StateFlow<List<String>> = _distinctMechanics.asStateFlow()

    private val _distinctWorkshops = MutableStateFlow<List<String>>(emptyList())
    val distinctWorkshops: StateFlow<List<String>> = _distinctWorkshops.asStateFlow()

    private val _distinctSparePartBrands = MutableStateFlow<List<String>>(emptyList())
    val distinctSparePartBrands: StateFlow<List<String>> = _distinctSparePartBrands.asStateFlow()

    private val _distinctSparePartModels = MutableStateFlow<List<String>>(emptyList())
    val distinctSparePartModels: StateFlow<List<String>> = _distinctSparePartModels.asStateFlow()

    private val _distinctDurabilityDays = MutableStateFlow<List<Int>>(emptyList())
    val distinctDurabilityDays: StateFlow<List<Int>> = _distinctDurabilityDays.asStateFlow()

    // Documentos de repuestos
    // Documentos de repuestos
    private val _sparePartDocuments = MutableStateFlow<List<DocumentUiModel>>(emptyList())
    val sparePartDocuments: StateFlow<List<DocumentUiModel>> = _sparePartDocuments.asStateFlow()

    init {
        viewModelScope.launch {
            mainInteractor.getMaintenances(viewModelScope.coroutineContext)
                .stateIn(
                    scope = viewModelScope,
                    started = SharingStarted.WhileSubscribed(),
                    initialValue = emptyList()
                )
                .collect {
                    // Obtener los mantenimientos con spare parts a través del interactor
                    _maintenances.value = mainInteractor.getMaintenancesByVehicleWithSpareParts(vehicle.vin)
                        .apply {
                            // Actualizamos el listado de mantenimientos del vehículo
                            if (vehicle.maintenances != this) {
                                Counter.count++
                                println("Updating maintenances for vehicle ${vehicle.vin} - ${Counter.count}")
                                vehicle.maintenances = this
                            }
                        }
                }
        }

        // Al iniciar el ViewModel, cargamos los mantenimientos desde el servidor
        fetchMaintenancesFromServer()

        // Cargar el catálogo de repuestos
        loadSparePartCatalog()

        // Cargar datos de autocompletado
        loadAutocompleteData()
    }

    /**
     * Obtiene los mantenimientos del vehículo desde el servidor y actualiza la base de datos local.
     * Esta función se llama automáticamente al iniciar el ViewModel, pero también puede ser llamada
     * manualmente para actualizar los datos.
     */
    fun fetchMaintenancesFromServer() {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val response = mainInteractor.fetchMaintenancesByVehicle(vehicle.vin)
                if (response is DResponse.Success) {
                    FileUtils.log("Maintenances fetched successfully: ${response.data.size} maintenances")
                    // Aquí limpiamos los mantenimientos para este vehículo y volvemos a establecerlos con los nuevos datos
                    mainInteractor.syncMaintenances(response.data)
                    _maintenances.value = mainInteractor.getMaintenancesByVehicleWithSpareParts(vehicle.vin)
                } else if (response is DResponse.Error) {
                    FileUtils.log("Error fetching maintenances: ${response.error}")
                }
            } catch (e: Exception) {
                FileUtils.log("Exception fetching maintenances: ${e.message}")
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun getMaintenances(): List<DMaintenance> {
        return _maintenances.value.filter { it.vehicleVin == vehicle.vin }
    }

    fun getMaintenanceById(id: Long): DMaintenance? {
        return _maintenances.value.firstOrNull { it.id == id }
    }

    /**
     * Añade un nuevo mantenimiento al vehículo.
     * @param maintenance El mantenimiento a añadir.
     * @return true si el mantenimiento se añadió correctamente, false en caso contrario.
     */
    /**
     * Añade un nuevo mantenimiento al vehículo.
     * @param maintenance El mantenimiento a añadir.
     * @return true si el mantenimiento se añadió correctamente, false en caso contrario.
     */
    suspend fun addMaintenance(maintenance: DMaintenance): Boolean {
        return try {
            // Chain the signature to the last maintenance and sign the spare parts
            val signedMaintenance = signNewMaintenance(maintenance, _maintenances.value)

            val response = withContext(AppDispatchers.IO) {
                mainInteractor.addMaintenance(signedMaintenance)
            }

            val success = response is DResponse.Success
            _lastAddResult.value = success

            // Limpiar repuestos temporales después de añadir el mantenimiento
            if (success) {
                clearTemporarySpareParts()
            }

            success
        } catch (e: Exception) {
            FileUtils.log("Error al añadir mantenimiento: ${e.message}")
            _lastAddResult.value = false
            false
        }
    }

    /**
     * Versión no suspendida que inicia la operación de añadir mantenimiento.
     * Útil para llamadas desde la UI que no pueden usar suspend.
     * @param maintenance El mantenimiento a añadir.
     * @param onResult Callback que se ejecuta con el resultado de la operación.
     */
    fun addMaintenanceAndNotify(maintenance: DMaintenance, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val result = addMaintenance(maintenance)
            onResult(result)
        }
    }

    fun updateMaintenanceAndNotify(maintenance: DMaintenance, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            try {
                // Get the last maintenance (excluding the one being edited if it was the last one)
                // Ideally, we should find the maintenance immediately preceding this one in date order
                // For simplicity in this iteration, we might just re-sign with the current state.
                // However, strict blockchain-like linking requires finding the previous block.
                // If we are editing, the previous signature should be the same as when it was created,
                // UNLESS the previous maintenance changed (which shouldn't happen if signed).
                // So we need to find the maintenance before this one.
                val sortedMaintenances = _maintenances.value.sortedBy { it.date }
                val index = sortedMaintenances.indexOfFirst { it.id == maintenance.id }
                val previousSignature = if (index > 0) sortedMaintenances[index - 1].signature else null

                val signature = SignatureService.signMaintenance(maintenance, previousSignature)

                // Sign spare parts
                val signedSpareParts = maintenance.spareParts.map {
                    it.copy(signature = SignatureService.signSparePart(it))
                }

                val signedMaintenance = maintenance.copy(
                    signature = signature,
                    spareParts = signedSpareParts
                )

                val response = withContext(AppDispatchers.IO) {
                    mainInteractor.updateMaintenance(signedMaintenance)
                }
                onResult(response is DResponse.Success)
            } catch (e: Exception) {
                FileUtils.log("Error updating maintenance: ${e.message}")
                onResult(false)
            }
        }
    }

    /**
     * Deletes a maintenance (soft delete)
     * Only unsigned maintenances can be deleted
     * @param maintenance The maintenance to delete
     * @param onResult Callback with success status and optional error message
     */
    fun deleteMaintenanceAndNotify(maintenance: DMaintenance, onResult: (Boolean, String?) -> Unit) {
        viewModelScope.launch {
            try {
                // Validate that it's not signed
                if (maintenance.isSigned) {
                    onResult(false, "No se puede eliminar un mantenimiento firmado")
                    return@launch
                }

                val response = withContext(AppDispatchers.IO) {
                    maintenance.id?.let { maintenanceId ->
                        mainInteractor.deleteMaintenance(maintenanceId, vehicle.vin)
                    } ?: DResponse.Error("Maintenance ID is null")
                }

                when (response) {
                    is DResponse.Success -> {
                        // Refresh maintenance list from server
                        fetchMaintenancesFromServer()
                        onResult(true, null)
                    }
                    is DResponse.Complete -> {
                        // Complete case - treat as success
                        fetchMaintenancesFromServer()
                        onResult(true, null)
                    }
                    is DResponse.Error -> {
                        onResult(false, response.error)
                    }
                }
            } catch (e: Exception) {
                FileUtils.log("Error deleting maintenance: ${e.message}")
                onResult(false, e.message)
            }
        }
    }

    fun startEditingMaintenance(maintenance: DMaintenance) {
        _temporarySpareParts.value = maintenance.spareParts
    }

    fun signMaintenance(maintenance: DMaintenance) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val response = withContext(AppDispatchers.IO) {
                    mainInteractor.signMaintenance(maintenance.id!!, vehicle.vin)
                }
                if (response is DResponse.Success) {
                    // Refresh maintenances
                    fetchMaintenancesFromServer()
                } else {
                    FileUtils.log("Error signing maintenance: ${(response as? DResponse.Error)?.error}")
                }
            } catch (e: Exception) {
                FileUtils.log("Exception signing maintenance: ${e.message}")
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun signAllMaintenances() {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val unsignedMaintenances = _maintenances.value.filterNot { it.isSigned }
                if (unsignedMaintenances.isNotEmpty()) {
                    unsignedMaintenances.forEach { maintenance ->
                        withContext(AppDispatchers.IO) {
                            mainInteractor.signMaintenance(maintenance.id!!, vehicle.vin)
                        }
                    }
                    fetchMaintenancesFromServer()
                }
            } catch (e: Exception) {
                FileUtils.log("Exception in signAllMaintenances: ${e.message}")
            } finally {
                _isLoading.value = false
            }
        }
    }

    // === FUNCIONES PARA GESTIÓN DE REPUESTOS TEMPORALES ===

    /**
     * Añade un repuesto temporal a la lista (para construcción del mantenimiento)
     */
    fun addTemporarySparePart(sparePart: DSparePart) {
        _temporarySpareParts.value = _temporarySpareParts.value + sparePart
    }

    /**
     * Elimina un repuesto temporal de la lista
     */
    fun removeTemporarySparePart(sparePart: DSparePart) {
        _temporarySpareParts.value = _temporarySpareParts.value.filter { it != sparePart }
    }

    /**
     * Limpia la lista de repuestos temporales
     */
    fun clearTemporarySpareParts() {
        _temporarySpareParts.value = emptyList()
    }

    /**
     * Obtiene la lista de repuestos temporales actual
     */
    fun getTemporarySpareParts(): List<DSparePart> {
        return _temporarySpareParts.value
    }

    // === FUNCIONES PARA GESTIÓN DEL CATÁLOGO ===

    /**
     * Carga el catálogo de repuestos desde el backend
     * TODO: Implementar cuando el backend esté listo
     */
    private fun loadSparePartCatalog() {
        viewModelScope.launch {
            try {
                // TODO: Cargar desde el backend cuando esté disponible
                _sparePartCatalog.value = DefaultSparePartCatalog
            } catch (e: Exception) {
                FileUtils.log("Error loading spare part catalog: ${e.message}")
            }
        }
    }

    /**
     * Carga los datos de autocompletado para los formularios
     */
    private fun loadAutocompleteData() {
        viewModelScope.launch {
            launch {
                mainInteractor.getDistinctMechanicsFlow(coroutineContext).collect { _distinctMechanics.value = it }
            }
            launch {
                mainInteractor.getDistinctWorkshopsFlow(coroutineContext).collect { _distinctWorkshops.value = it }
            }
            launch {
                // Pass the vehicle type to filter suggestions
                mainInteractor.getDistinctSparePartBrandsFlow(vehicle.vehicleType.code, coroutineContext).collect { _distinctSparePartBrands.value = it }
            }
            launch {
                // Pass the vehicle type to filter suggestions
                mainInteractor.getDistinctSparePartModelsFlow(vehicle.vehicleType.code, coroutineContext).collect { _distinctSparePartModels.value = it }
            }
            launch {
                mainInteractor.getDistinctDurabilityDaysFlow(coroutineContext).collect { _distinctDurabilityDays.value = it }
            }
        }
    }

    /**
     * Recarga los datos de autocompletado (útil después de añadir un nuevo mantenimiento)
     */
    fun refreshAutocompleteData() {
        // No op - data is now reactive using flows
    }

    /**
     * Obtiene los tipos de repuestos por categoría
     */
    fun getSparePartTypesByCategory(categoryId: String): List<DSparePartType> {
        return _sparePartCatalog.value?.sparePartTypes?.values
            ?.filter { it.categoryId == categoryId }
            ?: emptyList()
    }


    /**
     * Obtiene el último repuesto usado para este vehículo del mismo tipo
     * @param categoryId ID de la categoría del repuesto
     * @param sparePartId ID del tipo de repuesto
     * @return El último repuesto usado o null si no existe
     */
    suspend fun getLastUsedSparePartForType(categoryId: String, sparePartId: String): DSparePart? {
        return mainInteractor.getLatestSparePartByCategoryAndType(vehicle.vin, categoryId, sparePartId)
    }

    // === SPARE PART DOCUMENTS ===

    fun loadSparePartDocuments(sparePartId: Long) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                // 1. Get local documents
                val localDocs = mainInteractor.getSparePartDocuments(sparePartId)
                
                // 2. Get remote metadata
                val response = mainInteractor.getSparePartDocumentsMetadata(sparePartId)
                val remoteDocs = if (response is DResponse.Success) response.data else emptyList()
                
                // 3. Combine and determine status
                val combinedDocs = mutableListOf<DocumentUiModel>()
                
                // Process local docs
                localDocs.forEach { localDoc ->
                    val remoteDoc = remoteDocs.find { it.id == localDoc.id && it.id != 0L }
                    if (remoteDoc != null) {
                        combinedDocs.add(DocumentUiModel(localDoc, SyncStatus.SYNCED))
                    } else {
                        combinedDocs.add(DocumentUiModel(localDoc, SyncStatus.LOCAL_ONLY))
                    }
                }
                
                // Process remote only docs
                remoteDocs.forEach { remoteDoc ->
                    if (localDocs.none { it.id == remoteDoc.id }) {
                        combinedDocs.add(DocumentUiModel(remoteDoc, SyncStatus.REMOTE_ONLY))
                    }
                }
                
                _sparePartDocuments.value = combinedDocs
            } catch (e: Exception) {
                FileUtils.log("Error loading spare part documents: ${e.message}")
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun downloadSparePartDocument(document: DSparePartDocument) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val response = mainInteractor.downloadSparePartDocument(document.id)
                if (response is DResponse.Success) {
                    val fullDoc = response.data
                    if (fullDoc.fileBase64 != null) {
                        val path = FileUtils.saveBase64ToFile(fullDoc.fileBase64, fullDoc.name)
                        if (path.isNotEmpty()) {
                            // Update local DB with new path
                            val docToSave = fullDoc.copy(filePath = path)
                            // We need a way to insert/update local DB from here. 
                            // Since we don't have a direct "insert" in interactor for just saving, 
                            // we can use updateLocalSparePartDocumentPath if we assume it inserts if not exists, 
                            // or better, use uploadSparePartDocument which allows saving local.
                            // But upload tries to upload. 
                            // Let's rely on updateLocalSparePartDocumentPath to act as "save local".
                            // Checking implementation of updateLocalSparePartDocumentPath in GarageRepository, it does insert().
                            mainInteractor.updateLocalSparePartDocumentPath(docToSave, path)
                            
                            loadSparePartDocuments(document.sparePartId)
                        }
                    }
                } else {
                     FileUtils.log("Error downloading document: ${(response as? DResponse.Error)?.error}")
                }
            } catch (e: Exception) {
                 FileUtils.log("Error downloading document: ${e.message}")
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun uploadSparePartDocument(sparePart: DSparePart, fileData: FileData) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                // Save file locally
                // FileData already contains Base64 content
                val base64Content = fileData.fileContentBase64
                val localPath = FileUtils.saveBase64ToFile(base64Content, fileData.fileName)
                
                // Calculate size from Base64 length (approximate)
                // Base64 is 4 chars for 3 bytes. Padding might affect it slightly but this is close enough for MB display.
                val sizeBytes = (base64Content.length * 3L) / 4L
                
                val doc = DSparePartDocument(
                    id = 0, // ID will be ignored/generated by DB
                    sparePartId = sparePart.id ?: 0,
                    vehicleVin = vehicle.vin,
                    name = fileData.fileName,
                    filePath = localPath ?: "",
                    fileType = fileData.fileName.substringAfterLast('.', "unknown"),
                    createdAt = link.erpango.quip.utils.DatetimeUtils.current(),
                    sizeMb = sizeBytes / (1024.0 * 1024.0),
                    fileContentBase64 = base64Content
                )

                val response = mainInteractor.uploadSparePartDocument(doc)
                if (response is DResponse.Error) {
                    FileUtils.log("Error uploading document: ${response.error}")
                }
                loadSparePartDocuments(sparePart.id ?: 0)
            } catch (e: Exception) {
                FileUtils.log("Exception uploading document: ${e.message}")
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun deleteSparePartDocument(document: DSparePartDocument) {
        viewModelScope.launch {
             _isLoading.value = true
             try {
                 val response = mainInteractor.deleteSparePartDocument(document)
                 if (response is DResponse.Success) {
                     loadSparePartDocuments(document.sparePartId)
                 } else {
                     FileUtils.log("Error deleting document: ${(response as? DResponse.Error)?.error}")
                 }
             } catch (e: Exception) {
                 FileUtils.log("Exception deleting document: ${e.message}")
             } finally {
                 _isLoading.value = false
             }
        }
    }

    fun renameSparePartDocument(document: DSparePartDocument, newName: String) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                val response = mainInteractor.renameSparePartDocument(document, newName)
                if (response is DResponse.Success) {
                    loadSparePartDocuments(document.sparePartId)
                } else {
                    FileUtils.log("Error renaming document: ${(response as? DResponse.Error)?.error}")
                }
            } catch (e: Exception) {
                FileUtils.log("Exception renaming document: ${e.message}")
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun openSparePartDocument(document: DSparePartDocument) {
        viewModelScope.launch {
            val mimeType = when (document.fileType.lowercase()) {
                "pdf" -> "application/pdf"
                "png" -> "image/png"
                "jpg", "jpeg", "jpg" -> "image/jpeg"
                else -> "*/*"
            }

            // Check if local file exists first
            if (document.filePath.isNotEmpty() && FileUtils.fileExists(document.filePath)) {
                link.erpango.quip.utils.PlatformUtils.openFile(document.filePath, mimeType)
                return@launch
            }

            // Try to download
             val response = mainInteractor.downloadSparePartDocument(document.id)
             if (response is DResponse.Success && response.data.fileBase64 != null) {
                 val path = FileUtils.saveBase64ToFile(response.data.fileBase64!!, document.name)
                 if (path.isNotEmpty()) {
                     mainInteractor.updateLocalSparePartDocumentPath(document, path)
                     loadSparePartDocuments(document.sparePartId)
                     link.erpango.quip.utils.PlatformUtils.openFile(path, mimeType)
                 }
             } else {
                 // Error handling (toast/snackbar ideally)
                 println("Failed to download document for opening")
             }
        }
    }

    fun shareSparePartDocument(document: DSparePartDocument) {
        viewModelScope.launch {
            val mimeType = when (document.fileType.lowercase()) {
                "pdf" -> "application/pdf"
                "png" -> "image/png"
                "jpg", "jpeg" -> "image/jpeg"
                else -> "*/*"
            }
            
            // Check if local file exists first
            if (document.filePath.isNotEmpty() && FileUtils.fileExists(document.filePath)) {
                link.erpango.quip.utils.PlatformUtils.shareFile(document.filePath, mimeType)
                return@launch
            }

            // Try to download
             val response = mainInteractor.downloadSparePartDocument(document.id)
             if (response is DResponse.Success && response.data.fileBase64 != null) {
                 val path = FileUtils.saveBase64ToFile(response.data.fileBase64!!, document.name)
                 if (path.isNotEmpty()) {
                     mainInteractor.updateLocalSparePartDocumentPath(document, path)
                     loadSparePartDocuments(document.sparePartId)
                     link.erpango.quip.utils.PlatformUtils.shareFile(path, mimeType)
                 }
             } else {
                 println("Failed to download document for sharing")
             }
        }
    }
    fun retryUploadSparePartDocument(document: DSparePartDocument) {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                 // Ensure we have base64 content if not present on object but file exists
                val docToUpload = if (document.fileContentBase64.isNullOrEmpty() && document.filePath.isNotEmpty()) {
                     val base64 = FileUtils.readFileToBase64(document.filePath)
                     if (base64.isNotEmpty()) document.copy(fileContentBase64 = base64) else document
                } else document
                
                val response = mainInteractor.uploadSparePartDocument(docToUpload)
                if (response is DResponse.Error) {
                    FileUtils.log("Error uploading document: ${response.error}")
                }
                loadSparePartDocuments(document.sparePartId)
            } catch (e: Exception) {
                FileUtils.log("Exception uploading document: ${e.message}")
            } finally {
                _isLoading.value = false
            }
        }
    }
}
